import React from "react";
import { FieldConfig } from "../../../../../models/Models";
import { FieldArrayPath, FieldValues, Path, useFormContext } from "react-hook-form";
import ArrayFieldsRenderer from "./ArrayFieldsRenderer";
import TextField from "./TextField";
import SelectField from "./SelectField";
import FileUploadWithLocationUrlField from "./FileUploadWithLocationUrlField";

interface FormFieldsRendererProps<T extends FieldValues> {
    config: FieldConfig<T>[];
    basePath?: string;
}

const FormFieldsRenderer = <T extends FieldValues> ({
    config,
    basePath = "",
}: FormFieldsRendererProps<T>) => {
    const { register, formState } = useFormContext<T>();

    return (
        <div className={`flex flex-col gap-2`}>
            {
                config.map((field) => {
                    const path = (basePath ? `${basePath}.${field.path}` : field.path) as Path<T>;

                    if (field.type === "duration"){
                        const durationPattern = /^[0-9]{1,2}:([0-5][0-9]):([0-5][0-9])$/;
                        return (
                            <div key = {field.path} className={`flex flex-col gap-2`}>
                                <label className = {`dark:text-gray-100 mb-1`}>{field.label}</label>
                                <input
                                    type="text"
                                    className={`bg-gray-300 dark:bg-brand-900 w-full p-2 rounded-md placeholder:text-gray-900 dark:placeholder:text-gray-100`}
                                    {...register(path, { pattern: {
                                            value: durationPattern,
                                            message: "Invalid duration format. Must be HH:mm:ss",
                                        } }
                                    )}
                                    placeholder={"hh:mm:ss"}
                                    maxLength={8}
                                />
                                {
                                    formState && formState.errors && formState.errors[path]
                                    && <p className={`text-red-400`}>{`${formState.errors[path].message}`}</p>
                                }
                            </div>
                        );
                    }

                    if (field.isArray)
                        return (
                                <ArrayFieldsRenderer
                                    key={field.path}
                                    label={field.label ?? ""}
                                    config={field.fields}
                                    name={path as FieldArrayPath<T>}
                                    isAppendable={field.type === "array" ? field.isAppendable : true}
                                    areItemsRemovable={field.type === "array" ? field.areItemsRemovable : true}
                                />
                        );

                    if (field.fields && !field.isArray)
                        return (
                            <FormFieldsRenderer
                                key={field.path}
                                config={field.fields}
                                basePath={path}
                            />
                        );

                    if (field.type === "file")
                        return (
                            <FileUploadWithLocationUrlField key = {field.path} path={path} label={field.label} allowedFiles={field.allowedFiles}/>
                        );

                    if (field.type === "select")
                        return (
                            <SelectField key = {path} name={path} label={field.label} options={field.options} />
                        );

                    if (field.type === "text"
                        || field.type === "number"
                        || field.type === "date"
                        || field.type === "datetime-local"
                    )
                        return(
                            <TextField key={path} name={path} label={field.label} type={field.type} />
                        );

                    if(field.type === "paragraph")
                        return (
                            <div key = {field.path} className={`flex flex-col gap-2`}>
                                <label className = {`dark:text-gray-100`}>{field.label}</label>
                                <textarea
                                    {...register(path)}
                                    className = {`p-2 rounded-md h-28 w-full bg-gray-300 dark:bg-brand-900`}
                                />
                            </div>
                        );

                    return (<></>);
                })
            }
        </div>
    );
};

export default FormFieldsRenderer;